import { DateTime, DateTimeUnit } from 'luxon'
import pluralize from 'pluralize'

export interface UtcRange {
  lower: string | null // ISO UTC string with .SSS and trailing Z
  upper: string | null // ISO UTC string with .SSS and trailing Z
  lowerInclusive: boolean
  upperInclusive: boolean
}

export function parseDateValueToUtc(
  rangeExpr: string,
  timeZone: string,
  nowUtcIso: string = DateTime.utc().toISO(),
): UtcRange {
  return parseDateValue(rangeExpr, timeZone, nowUtcIso, 'DATETIME')
}

export function parseDateValue(
  rangeExpr: string,
  timeZone: string,
  nowUtcIso: string = DateTime.utc().toISO(),
  type: 'DATE' | 'DATETIME' = 'DATETIME',
): UtcRange {
  const { lower, upper, lowerInclusive, upperInclusive } =
    parseRangeOnly(rangeExpr)

  return {
    lowerInclusive,
    upperInclusive,
    lower:
      lower && lower !== '*'
        ? evaluateEndpoint(lower, 'lower', timeZone, nowUtcIso, type)
        : null,
    upper:
      upper && upper !== '*'
        ? evaluateEndpoint(upper, 'upper', timeZone, nowUtcIso, type)
        : null,
  }
}

const ISO_Z_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

export interface ParsedRangeOnly {
  lower: string
  upper: string
  lowerInclusive: boolean
  upperInclusive: boolean
}

export function extractRangeParts(expr: string) {
  const trimmed = expr.trim()
  if (trimmed.length < 2) return null

  const leftBracket = trimmed[0]
  const rightBracket = trimmed.at(-1)
  if (
    (leftBracket !== '[' && leftBracket !== '{') ||
    (rightBracket !== ']' && rightBracket !== '}')
  ) {
    return null
  }

  const inside = trimmed.slice(1, -1).trim()
  const parts = inside.split(/TO/)
  if (parts.length !== 2) return null

  const [lower, upper] = parts.map((p) => p.trim())
  if (!lower || !upper) return null

  return { leftBracket, rightBracket, lower, upper }
}

export function parseRangeOnly(expr: string): ParsedRangeOnly {
  const rangeParts = extractRangeParts(expr)

  if (!rangeParts) {
    throw new Error(`Invalid range expression: ${expr}`)
  }

  const { leftBracket, rightBracket, lower, upper } = rangeParts

  return {
    lower,
    upper,
    lowerInclusive: leftBracket === '[',
    upperInclusive: rightBracket === ']',
  }
}

// ------------------------
// Endpoint evaluation
// ------------------------

export function evaluateEndpoint(
  endpoint: string,
  which: 'lower' | 'upper',
  timeZone: string,
  nowUtc: string,
  type: 'DATE' | 'DATETIME' = 'DATETIME',
): string {
  // 1) Absolute UTC instant
  if (ISO_Z_RE.test(endpoint)) {
    const dt = DateTime.fromISO(endpoint, { zone: 'utc' })
    return type === 'DATE'
      ? dt.setZone(timeZone).toFormat('yyyy-MM-dd')
      : dtToIsoZ(dt)
  }

  // 2) Local date only
  if (DATE_ONLY_RE.test(endpoint)) {
    if (type === 'DATE') return endpoint
    const base = DateTime.fromISO(endpoint, { zone: timeZone })
    const dt = which === 'lower' ? base.startOf('day') : base.endOf('day')
    return dtToIsoZ(dt.toUTC())
  }

  // 3) NOW date math
  if (endpoint.startsWith('NOW')) {
    const local = evaluateNowMath(endpoint, timeZone, nowUtc) // returns DateTime in timeZone
    return type === 'DATE'
      ? local.toFormat('yyyy-MM-dd')
      : dtToIsoZ(local.toUTC())
  }

  throw new Error(`Unsupported endpoint: ${endpoint}`)
}

// ------------------------
// NOW math (subset tailored to your inputs)
// Supports: +/-, units (MILLISECOND..YEAR), rounding with /UNIT, and :DATE/:DATETIME casts
// ------------------------

// ([+\-/:])(\d+)?(TOKEN)
const OP_RE = /([+\-/:])(\d+)?([A-Z]+)\b/g
const VALID_TOKENS = new Set([
  'MILLISECOND',
  'MILLISECONDS',
  'SECOND',
  'SECONDS',
  'MINUTE',
  'MINUTES',
  'HOUR',
  'HOURS',
  'DAY',
  'DAYS',
  'WEEK',
  'WEEKS',
  'MONTH',
  'MONTHS',
  'YEAR',
  'YEARS',
  'DATE',
  'DATETIME',
])

const VALID_UNITS = [
  'MILLISECOND',
  'SECOND',
  'MINUTE',
  'HOUR',
  'DAY',
  'WEEK',
  'MONTH',
  'YEAR',
] as const
const VALID_UNIT_SET = new Set<string>(VALID_UNITS)

export type Unit = (typeof VALID_UNITS)[number]

function normalizeUnit(unit: string): Unit {
  if (VALID_UNIT_SET.has(unit)) {
    return pluralize.singular(unit) as Unit
  }
  throw new Error(`Invalid unit: ${unit}`)
}

function evaluateNowMath(
  expr: string,
  timeZone: string,
  nowUtc: string,
): DateTime {
  let dt = DateTime.fromISO(nowUtc, { zone: 'utc' }).setZone(timeZone)

  OP_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = OP_RE.exec(expr))) {
    const op = m[1]
    const amountStr = m[2]
    const token = m[3]

    if (!VALID_TOKENS.has(token)) throw new Error(`Invalid token: ${token}`)

    if (op === ':') {
      if (token === 'DATE') {
        // Cast to date only: immediately floor to start of local day
        dt = dt.startOf('day')
      } else if (token === 'DATETIME') {
        // Leave time as is
      } else throw new Error(`Invalid cast: :${token}`)
      continue
    }

    if (op === '/') {
      const unit = normalizeUnit(token)
      dt = roundLuxon(dt, unit)
      continue
    }

    // + or -
    const n = Number(amountStr ?? '0')
    const unit = normalizeUnit(token)
    dt = addLuxon(dt, op === '+' ? n : -n, unit)
  }

  return dt // still in local zone
}

function addLuxon(dt: DateTime, amount: number, unit: Unit): DateTime {
  if (amount === 0) return dt
  return dt.plus({ [pluralize.plural(unit)]: amount })
}

function roundLuxon(dt: DateTime, unit: Unit): DateTime {
  switch (unit) {
    case 'SECOND':
    case 'MINUTE':
    case 'HOUR':
    case 'DAY':
    case 'MONTH':
    case 'YEAR':
      return dt.startOf(unit.toLowerCase() as DateTimeUnit)
    case 'MILLISECOND':
      return dt
    case 'WEEK': {
      // Monday-based week start
      const monday = dt.startOf('day').minus({ days: dt.weekday - 1 })
      return monday.startOf('day')
    }
  }
}

function dtToIsoZ(dtUtc: DateTime): string {
  const utc = dtUtc.toUTC()
  return utc.toFormat("yyyy-LL-dd'T'HH:mm:ss.SSS'Z'")
}
